import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { CheckDto } from "src/app/shared/models/check-dto";
import { PosMenu } from "src/app/shared/models/DTO/pos-menu.dto";
import { AppMessageSeverity } from "src/app/shared/models/enums/AppMessageSeverity.enum";
import { EmployeeRecord } from "src/app/shared/models/focusDataModels/EmployeeRecord";
import { VenueCoreConfig } from "src/app/shared/models/focusDataModels/VenueCoreConfig";
import { AppService } from "./app.service";
import { FocusdataService } from "./focusdata.service";
import { FocuslinkService } from "./focuslink.service";
import { OrderService } from "./order.service";
import { OOVenueService } from "./venue-service";

@Injectable({
	providedIn: "root",
})
export class PosService {
	checkList: BehaviorSubject<CheckDto[]> = new BehaviorSubject<CheckDto[]>([]);

	loggedInEmployee: BehaviorSubject<EmployeeRecord> = new BehaviorSubject<EmployeeRecord>(new EmployeeRecord());

	posConfigurationMenu: BehaviorSubject<PosMenu> = new BehaviorSubject<PosMenu>(new PosMenu());

	venueCoreConfig: BehaviorSubject<VenueCoreConfig> = new BehaviorSubject<VenueCoreConfig>(new VenueCoreConfig());

	venueCommands: {
		name: string;
		faIcon: string;
		faPrefix: string;
		order: number;
		display: boolean;
		disabled?: any;
	}[] = [];
	venueCommandAction: Subject<venueCommandActionModel> = new Subject<venueCommandActionModel>();

	constructor(
		public orderService: OrderService,
		public venueService: OOVenueService,
		public app: AppService,
		private focuslink: FocuslinkService,
		private focusdata: FocusdataService
	) {}

	async checksGetAll() {
		const resp = await this.focuslink.checksGetByParams(this.venueService.venueKey, `businessDate=${this.app.moment().format("YYYY-MM-DD")}`);

		this.checkList.next(resp.map((check) => new CheckDto(check)));
		return this.checkList.value;
	}

	async posCommandAction(command: string) {
		switch (command.toLowerCase()) {
			case "quit": {
				this.logOutEmployee();
				break;
			}
			case "print": {
				this.venueCommandAction.next({
					command: "print",
					payload: {
						checkKey: this.orderService.activeOrder.value.checkKey,
						seatKey: this.orderService.activeSeat.value.seatKey,
						checkNumber: this.orderService.activeOrder.value.checkNumber,
					},
				});
			}
			case "checks": {
				this.orderService.activeOrder.next(new CheckDto());
				break;
			}
		}
	}

	async initPos(venueKey) {
		const venue = await this.focuslink.venueGet(venueKey);
		this.venueService.refreshVenue(venue);
		this.venueService.subscriptionVenueMessage.subscribe((data) => {
			if (data.message === "posCheckSave") {
				let existing = this.checkList.value.find((check) => check.checkKey === data.payload.checkKey);
				if (existing) {
					existing = new CheckDto(data.payload);
				} else {
					this.checkList.value.push(new CheckDto(data.payload));
				}
				this.checkList.next(this.checkList.value);
			}
		});
		this.venueCoreConfigGet();
		this.posMenuGet();
		this.checksGetAll();

		this.venueCommandsGet();
	}

	async logInEmployee(accessCode: string) {
		try {
			const resp = await this.focusdata.employeeGetByAccessCode(this.venueService.venueKey, accessCode);
			this.loggedInEmployee.next(new EmployeeRecord(resp));
			this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: "", detail: `Welcome in ${this.loggedInEmployee.value.NickName}` });
			return this.loggedInEmployee;
		} catch (error) {
			this.app.msgSvc.add({ severity: AppMessageSeverity.RED, summary: "", detail: `Access code invalid` });
		}
	}

	async logOutEmployee() {
		this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: "", detail: `Bye bye ${this.loggedInEmployee.value.NickName}. See you soon!` });
		this.loggedInEmployee.next(new EmployeeRecord());
		return this.loggedInEmployee.value;
	}

	async posMenuGet() {
		const resp = await this.focuslink.posMenuGet(this.venueService.venueKey);
		this.posConfigurationMenu.next(new PosMenu(resp));
		return this.posConfigurationMenu.value;
	}

	async venueCommandsGet() {
		this.venueCommands = [
			{
				name: "Quit",
				faIcon: "sign-out-alt",
				faPrefix: "fad",
				order: 1,
				display: true,
			},
			{
				name: "Checks",
				faIcon: "clipboard-check",
				faPrefix: "fad",
				order: 2,
				display: true,
			},
			{
				name: "Print",
				faIcon: "print",
				faPrefix: "fad",
				order: 3,
				display: true,
			},
			{
				name: "Delete",
				faIcon: "minus",
				faPrefix: "fad",
				order: 4,
				display: true,
				disabled: !this.orderService.activeOrder.value.checkKey,
			},
			{
				name: "Repeat",
				faIcon: "repeat",
				faPrefix: "fad",
				order: 5,
				display: true,
				disabled: !this.orderService.activeOrder.value.checkKey,
			},
			{
				name: "Send",
				faIcon: "paper-plane",
				faPrefix: "fad",
				order: 6,
				display: true,
				disabled: !this.orderService.activeOrder.value.checkKey,
			},
			{
				name: "Hold",
				faIcon: "hand-holding-box",
				faPrefix: "fad",
				order: 7,
				display: true,
				disabled: !this.orderService.activeOrder.value.checkKey,
			},
			{
				name: "Quit",
				faIcon: "sign-out-alt",
				faPrefix: "fad",
				order: 8,
				display: false,
			},
			{
				name: "Quit",
				faIcon: "sign-out-alt",
				faPrefix: "fad",
				order: 9,
				display: false,
			},
			{
				name: "Quit",
				faIcon: "sign-out-alt",
				faPrefix: "fad",
				order: 10,
				display: false,
			},
		];

		this.venueCommands.sort((a, b) => (a.order > b.order ? 1 : -1));
	}

	async venueCoreConfigGet() {
		const resp = await this.focusdata.coreConfigGet(this.venueService.venueKey);
		this.venueCoreConfig.next(new VenueCoreConfig(resp));
	}
}

interface venueCommandActionModel {
	command: string;
	payload: any;
}
